import { motion } from 'motion/react';
import { Archive, Check, FolderPlus, MoreHorizontal, OctagonX, Tag, Trash2, X } from 'lucide-react';
import { PanelTitle } from '@reflex/ui/components/layout/Typography';
import { Button } from '@reflex/ui/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@reflex/ui/components/ui/dropdown-menu';
import { cn } from '@reflex/ui/lib/utils';

export interface AgentSelectionBarProps {
  /** Number of agents currently checked. */
  count: number;
  /** Total number of selectable agents in view. */
  total: number;
  /** Whether every selectable agent is checked. */
  allSelected: boolean;
  /** Whether the selection contains at least one agent that can be archived (i.e. not already archived). */
  hasArchivable: boolean;
  /** Whether the selection contains at least one agent that is mid-turn and can be interrupted. */
  hasInterruptible: boolean;
  /** Select all when not all are selected, otherwise clear the selection. */
  onToggleAll: () => void;
  onArchive: () => void;
  /** Interrupt the in-flight turn of every selected agent that is mid-turn. Never deletes. */
  onStop: () => void;
  /** File the selection into a group (opens the group picker). */
  onAddToGroup: () => void;
  /** Label the selection (opens the label picker). */
  onAddLabel: () => void;
  onDelete: () => void;
  /** Leave select mode (also clears the selection). */
  onClose: () => void;
}

/**
 * Contextual bulk-action bar shown only while the sidebar is in select mode.
 * Keeps the idle sidebar uncluttered: it slides in from the bottom and carries
 * the whole multi-select affordance (count, select-all/clear, and actions).
 *
 * Action emphasis follows how often each is used. Group, Stop (interrupt the
 * current turn) and Archive are the common, non-destructive actions and sit
 * inline — grouping is the reason most multi-selections are made at all.
 * Delete is rare and irreversible, so it lives in an overflow menu rather than
 * being the visually primary action. Stop only appears when the selection
 * actually contains an agent mid-turn to interrupt.
 */
export function AgentSelectionBar({
  count,
  total,
  allSelected,
  hasArchivable,
  hasInterruptible,
  onToggleAll,
  onArchive,
  onStop,
  onAddToGroup,
  onAddLabel,
  onDelete,
  onClose,
}: AgentSelectionBarProps) {
  const nothingSelected = count === 0;
  return (
    <motion.div
      data-testid="agent-selection-bar"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: 16 }}
      transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
      className="@container shrink-0 border-t border-border/60 bg-panel/95 px-3 py-2 backdrop-blur"
    >
      <div className="flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={onToggleAll}
          aria-label={allSelected ? 'Clear selection' : 'Select all'}
          className="flex min-w-0 items-center gap-2 rounded-md px-1 py-0.5 text-muted-foreground transition-colors hover:text-foreground"
        >
          <span
            aria-hidden
            className={cn(
              'flex h-4 w-4 shrink-0 items-center justify-center rounded-[4px] border transition-colors',
              count > 0
                ? 'border-ring bg-ring text-background'
                : 'border-muted-foreground/50 bg-transparent',
            )}
          >
            {allSelected ? (
              <Check className="h-3 w-3" strokeWidth={3} />
            ) : count > 0 ? (
              <span className="h-0.5 w-2 rounded-full bg-background" />
            ) : null}
          </span>
          <PanelTitle as="span" size="sm" className="min-w-0 truncate font-normal">
            <span data-testid="agent-selection-count">
              {nothingSelected ? 'Select items' : `${count} selected`}
            </span>
          </PanelTitle>
        </button>

        {/* Actions never shrink; on a narrow sidebar the labels collapse to
            icon-only (container query) so nothing overlaps the count. */}
        <div className="flex shrink-0 items-center gap-0.5">
          <Button
            variant="ghost"
            size="sm"
            onClick={onAddToGroup}
            disabled={nothingSelected}
            aria-label="Add to group"
            title="Add to group"
            data-testid="agent-selection-group"
          >
            <FolderPlus className="h-3.5 w-3.5" />
            <span className="hidden @[20rem]:inline">Group</span>
          </Button>
          {hasInterruptible && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onStop}
              disabled={nothingSelected}
              aria-label="Stop"
              title="Stop the current turn"
              data-testid="agent-selection-stop"
            >
              <OctagonX className="h-3.5 w-3.5" />
              <span className="hidden @[20rem]:inline">Stop</span>
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon-sm"
                disabled={nothingSelected}
                aria-label="More actions"
                title="More actions"
                data-testid="agent-selection-more"
                className="text-muted-foreground"
              >
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {/* Labelling in bulk is rarer than grouping — labels usually
                  arrive from the classifier — so it sits here rather than
                  taking a second inline slot from the row of actions. */}
              <DropdownMenuItem onSelect={onAddLabel} data-testid="agent-selection-label">
                <Tag className="h-3.5 w-3.5" />
                Add label
              </DropdownMenuItem>
              <DropdownMenuItem
                variant="destructive"
                onSelect={onDelete}
                data-testid="agent-selection-delete"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          {hasArchivable && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onArchive}
              disabled={nothingSelected}
              aria-label="Archive"
              title="Archive"
              data-testid="agent-selection-archive"
            >
              <Archive className="h-3.5 w-3.5" />
              <span className="hidden @[20rem]:inline">Archive</span>
            </Button>
          )}
          <Button
            variant="ghost"
            size="icon-sm"
            onClick={onClose}
            aria-label="Done"
            data-testid="agent-selection-done"
            className="text-muted-foreground"
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      </div>
      <span className="sr-only">{total} agents available to select</span>
    </motion.div>
  );
}
